import './abrechnung.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { canShowPdf, PdfPages } from '../../components/Pdf';
import { fmtDate } from '../../lib/format';
import { daysUntil, eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import type { DraftResponse } from './types';

// Rechnung schreiben (Admin): Die offenen Positionen des Mandanten sind vorbelegt (gebuchte Monate, erledigte
// Unterlagen). Text und Betrag lassen sich ändern, Positionen entfernen (sie bleiben dann „zu berechnen“) oder
// dazuschreiben. „Weiter zur Vorschau“ zeigt die Rechnung als PDF; erst „Ja, Rechnung senden“ vergibt die Nummer,
// speichert die Rechnung und schickt sie dem Mandanten (Reiter „Rechnungen“, Benachrichtigung in der App).

interface Pos {
  key: string;
  itemId: string | null;
  description: string;
  net: string;
}
interface Body {
  positions: { itemId: string | null; description: string; net: number }[];
  invoiceDate: string;
  dueDate: string;
}

// 'YYYY-MM-DD' plus Tage
const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
// „1.500,00“ fürs Eingabefeld
const amountText = (v: number) => v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cents = (v: number) => Math.round(v * 100);
// Nennt der Text einen Mitarbeiter (ganzer Name oder Nachname)? Die Rechnung sehen FiBu, Steuerberater und Prüfer.
const namesEmployee = (text: string, names: string[]) => {
  const low = text.toLowerCase();
  const words = low.split(/[^\p{L}]+/u);
  return names.some((n) => {
    const last = (n.split(' ').pop() ?? '').toLowerCase();
    return low.includes(n.toLowerCase()) || (last.length >= 4 && words.includes(last));
  });
};
const validNet = (text: string) => {
  const v = parseEuro(text);
  return typeof v === 'number' && v > 0 && v < 1e6 ? v : null;
};

export function RechnungSchreiben({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose(): void }) {
  const toast = useToast();
  const res = useApi<DraftResponse>(`/api/kanzlei/abrechnung/${clientId}/draft`);
  // null: noch nichts geändert, dann gelten die offenen Positionen aus dem Entwurf
  const [pos, setPos] = useState<Pos[] | null>(null);
  const [invoiceDate, setInvoiceDate] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ url: string; body: Body } | null>(null);
  const keys = useRef(0);

  // Vorschau-PDF wieder freigeben
  useEffect(() => {
    if (!preview) return;
    return () => URL.revokeObjectURL(preview.url);
  }, [preview]);

  const d = res.data;
  const title = `Rechnung an ${clientName}`;
  if (!d) {
    return (
      <Dialog open onClose={onClose} title={title} className="abrdlg">
        {res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />}
      </Dialog>
    );
  }
  if (!d.ready) {
    return (
      <Dialog open onClose={onClose} title={title} className="abrdlg">
        <div className="lock">
          Bitte zuerst unter „Abrechnung“ die Angaben für die Rechnung eintragen (Name, Anschrift, Steuernummer, Bank).
        </div>
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Schließen
          </button>
        </div>
      </Dialog>
    );
  }

  const list: Pos[] =
    pos ?? d.items.map((i) => ({ key: i.id, itemId: i.id, description: i.description, net: amountText(i.net) }));
  const inv = invoiceDate ?? d.invoiceDate;
  // Fällig: Zahlungsziel ab Rechnungsdatum, bis jemand das Datum selbst ändert
  const due = dueDate ?? addDays(inv, daysUntil(d.dueDate, d.invoiceDate));
  const net = list.reduce((s, p) => s + cents(validNet(p.net) ?? 0), 0);
  const vat = Math.round((net * d.vatRate) / 100);
  const left = d.items.filter((i) => !list.some((p) => p.itemId === i.id)).length;
  const named = list.flatMap((p, i) => (namesEmployee(p.description, d.employeeNames) ? [i + 1] : []));

  const change = (next: Pos[]) => {
    setPos(next);
    setError(null);
  };
  const edit = (key: string, v: Partial<Pos>) => change(list.map((p) => (p.key === key ? { ...p, ...v } : p)));

  const check = (): Body | null => {
    if (!list.length) return fail('Bitte mindestens eine Position eintragen.');
    if (list.some((p) => !p.description.trim())) return fail('Bitte bei jeder Position die Leistung eintragen.');
    if (list.some((p) => validNet(p.net) === null)) return fail('Bitte bei jeder Position den Betrag prüfen, z. B. 150,00.');
    if (!inv) return fail('Bitte das Rechnungsdatum prüfen.');
    if (inv > d.invoiceDate) return fail('Das Rechnungsdatum liegt in der Zukunft. Bitte prüfen.');
    if (!due) return fail('Bitte das Fälligkeitsdatum prüfen.');
    if (due < inv) return fail('Die Fälligkeit liegt vor dem Rechnungsdatum. Bitte prüfen.');
    return {
      positions: list.map((p) => ({ itemId: p.itemId, description: p.description.trim(), net: validNet(p.net)! })),
      invoiceDate: inv,
      dueDate: due,
    };
  };
  function fail(text: string) {
    setError(text);
    return null;
  }

  // Eine Position ist inzwischen berechnet oder verworfen: Entwurf neu laden
  const handle = async (err: unknown) => {
    if (err instanceof ApiError && err.code === 'item_changed') {
      setPos(null);
      await res.reload();
      setError(
        'Eine Position ist inzwischen nicht mehr offen (schon berechnet oder verworfen). Die Positionen sind neu geladen, bitte prüfen.',
      );
    } else {
      setError(errorText(err));
    }
  };

  const showPreview = async (e: FormEvent) => {
    e.preventDefault();
    const body = check();
    if (!body) return;
    setBusy(true);
    try {
      const blob = await api.blob(`/api/kanzlei/abrechnung/${clientId}/preview`, body);
      setPreview({ url: URL.createObjectURL(blob), body });
    } catch (err) {
      await handle(err);
    } finally {
      setBusy(false);
    }
  };

  const send = async (body: Body) => {
    setBusy(true);
    try {
      const r = await api.post<{ id: string; number: string; amount: number }>(
        `/api/kanzlei/abrechnung/${clientId}/invoice`,
        body,
      );
      toast(`Rechnung ${r.number} über ${eurC(r.amount)} gesendet · ${clientName} sieht sie in der App`);
      onClose();
    } catch (err) {
      setPreview(null);
      await handle(err);
      setBusy(false);
    }
  };

  const sums = (
    <dl className="abr-sum">
      {d.vatRate ? (
        <>
          <dt>Summe netto</dt>
          <dd className="num">{eurC(net / 100)}</dd>
          <dt>zzgl. {d.vatRate} % Umsatzsteuer</dt>
          <dd className="num">{eurC(vat / 100)}</dd>
        </>
      ) : null}
      <dt className="abr-total">Rechnungsbetrag</dt>
      <dd className="num abr-total">{eurC((net + vat) / 100)}</dd>
    </dl>
  );

  if (preview) {
    const n = preview.body.positions.length;
    return (
      <Dialog open onClose={onClose} title={title} className="abrdlg">
        <div className="abr-prev">
          {canShowPdf() ? (
            <iframe src={preview.url} title="Vorschau der Rechnung" />
          ) : (
            <PdfPages
              url={preview.url}
              title="Vorschau der Rechnung"
              fallback={<p className="note">Die Vorschau kann hier nicht angezeigt werden.</p>}
            />
          )}
        </div>
        <p className="note abr-open">
          <a href={preview.url} target="_blank" rel="noopener noreferrer">
            Vorschau in neuem Fenster öffnen
          </a>
        </p>
        <div className="confirmup">
          <b>Rechnung an {clientName} senden?</b>
          <div>
            Rechnungsbetrag <b>{eurC((net + vat) / 100)}</b> · {n} {n === 1 ? 'Position' : 'Positionen'} · fällig am{' '}
            {fmtDate(preview.body.dueDate)}
          </div>
          <p className="note abr-m0">
            Danach sieht {clientName} die Rechnung mit PDF in der App unter „Rechnungen“ und bekommt eine Benachrichtigung. Ändern
            lässt sie sich dann nicht mehr, nur stornieren.
          </p>
          <div className="dlg-actions abr-start">
            <button type="button" className="btn dark" disabled={busy} onClick={() => void send(preview.body)}>
              Ja, Rechnung senden
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => setPreview(null)}>
              Ändern
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title={title} className="abrdlg">
      <p className="note abr-to">
        {d.client.name} · {d.client.street}, {d.client.zip} {d.client.city}
        {d.client.number ? ` · Kundennummer ${d.client.number}` : ''}
      </p>
      <form className="form" onSubmit={showPreview} noValidate>
        <div className="tw">
          <table className="abr-pos">
            <thead>
              <tr>
                <th>Leistung</th>
                <th className="abr-r">Netto (€)</th>
                <th>
                  <span className="sr-only">Entfernen</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={3} className="note">
                    Keine Positionen. Bitte eine Position hinzufügen.
                  </td>
                </tr>
              ) : (
                list.map((p, i) => (
                  <tr key={p.key}>
                    <td>
                      <input
                        aria-label={`Position ${i + 1}: Leistung`}
                        value={p.description}
                        maxLength={200}
                        placeholder="z. B. Lohnabrechnung Oktober 2026"
                        onChange={(e) => edit(p.key, { description: e.target.value })}
                      />
                    </td>
                    <td className="abr-amt">
                      <input
                        aria-label={`Position ${i + 1}: Betrag netto`}
                        className="num"
                        value={p.net}
                        inputMode="decimal"
                        placeholder="0,00"
                        onChange={(e) => edit(p.key, { net: e.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="abr-x"
                        aria-label={`Position ${i + 1} entfernen`}
                        onClick={() => change(list.filter((x) => x.key !== p.key))}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div>
          <button
            type="button"
            className="btn sm"
            onClick={() => change([...list, { key: `neu-${++keys.current}`, itemId: null, description: '', net: '' }])}
          >
            + Position hinzufügen
          </button>
        </div>
        {left > 0 && (
          <p className="note abr-m0">
            {left === 1 ? 'Eine entfernte Position bleibt' : `${left} entfernte Positionen bleiben`} unter „Zu berechnen“ und
            {left === 1 ? ' kommt' : ' kommen'} auf die nächste Rechnung.
          </p>
        )}
        <div className="row abr-two">
          <label>
            Rechnungsdatum
            <input
              type="date"
              value={inv}
              max={d.invoiceDate}
              onChange={(e) => {
                setInvoiceDate(e.target.value);
                setError(null);
              }}
            />
          </label>
          <label>
            Fällig am
            <input
              type="date"
              value={due}
              onChange={(e) => {
                setDueDate(e.target.value);
                setError(null);
              }}
            />
          </label>
        </div>
        {sums}
        {!d.vatRate && <p className="note abr-m0">Ohne Umsatzsteuer: Kleinunternehmer nach § 19 UStG.</p>}
        {named.length > 0 && (
          <p className="note abr-warn" role="status">
            {named.length === 1 ? `Position ${named[0]} nennt` : `Die Positionen ${named.join(', ')} nennen`} einen Mitarbeiter.
            Auf der Rechnung besser ohne Namen, z. B. „Unterlage Lohnbuchhaltung“.
          </p>
        )}
        {error && (
          <p className="note abr-err" role="alert">
            {error}
          </p>
        )}
        <div className="dlg-actions abr-start">
          <button type="submit" className="btn dark" disabled={busy}>
            Weiter zur Vorschau
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
        </div>
        <p className="note abr-m0">
          Rechnungsnummer {d.number} (wird beim Senden vergeben). Die Rechnung geht erst nach Ihrer Bestätigung an den Mandanten.
        </p>
      </form>
    </Dialog>
  );
}
